/**
 * Pure helpers for gathering relationship lines to render between selected
 * objects and their related (linked) objects on the canvas.
 *
 * - Outgoing: each selected element's `data.properties[propertyKey]` AND
 *   `data.properties[inversePropertyKey]` is inspected for IDs of linked
 *   targets. Both directions matter because the editor stores bidirectional
 *   relationships symmetrically (see `relationshipUtils.ts:401-411`).
 * - Incoming: every other element on the graph is scanned the same way for
 *   relations pointing INTO any selected element.
 * - Orphan targets (id not present in the graph) are skipped silently.
 * - Self-links (source === target) are skipped.
 * - The same logical relation seen from both sides (forward + inverse) is
 *   collapsed into a single line marked `bidirectional: true`.
 */

import { dia } from '@joint/core'
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'

export interface RelationshipLine {
  sourceId: string
  targetId: string
  relationKey: string
  /**
   * True when both `(source → target)` and `(target → source)` exist for the
   * same `relationKey` — i.e. the relation is symmetric in the underlying
   * data. Renderers can use this to draw the line with a distinct style
   * (e.g. dashed stroke) instead of two overlapping lines.
   */
  bidirectional: boolean
}

/**
 * Reads a property value (string | string[] | null/undefined / non-string) and
 * normalizes it into an array of valid id strings.
 */
function normalizeIds(value: unknown): string[] {
  if (value == null) return []
  const raw = Array.isArray(value) ? value : [value]
  const out: string[] = []
  for (const v of raw) {
    if (typeof v === 'string' && v.length > 0) out.push(v)
  }
  return out
}

/**
 * Returns the list of property keys that may carry linked-id arrays for a
 * given relation type. Includes `inversePropertyKey` when the template
 * defines a bidirectional relation.
 */
function relationKeysToScan(rel: TemplateRelationType): string[] {
  const keys: string[] = [rel.propertyKey]
  if (rel.inversePropertyKey) keys.push(rel.inversePropertyKey)
  return keys
}

/**
 * A relationship source/target reduced to the only fields the collector needs:
 * its id and its `data.properties` bag. Both the JointJS graph (editor) and the
 * canvas element list (viewer) project onto this shape, so the core algorithm
 * stays renderer-agnostic.
 */
export interface RelationshipEntity {
  id: string
  properties: Record<string, unknown>
}

/**
 * Renderer-agnostic core of {@link collectRelationshipLines}. Operates on a flat
 * entity list instead of a dia.Graph so the canvas viewer can reuse the exact
 * outgoing/incoming/bidirectional-collapse logic without a JointJS paper.
 */
export function collectRelationshipLinesFromEntities(
  selectedIds: ReadonlySet<string>,
  entities: readonly RelationshipEntity[],
  relationTypes: Record<string, TemplateRelationType> | undefined,
): RelationshipLine[] {
  if (!relationTypes) return []
  if (selectedIds.size === 0) return []

  const byId = new Map<string, RelationshipEntity>()
  for (const e of entities) byId.set(e.id, e)

  const raw: Array<{ sourceId: string; targetId: string; relationKey: string }> = []
  const seenRaw = new Set<string>()
  const relationEntries = Object.entries(relationTypes)

  const tryAdd = (sourceId: string, targetId: string, relationKey: string): void => {
    if (sourceId === targetId) return
    if (!byId.has(targetId)) return
    const k = `${sourceId}|${targetId}|${relationKey}`
    if (seenRaw.has(k)) return
    seenRaw.add(k)
    raw.push({ sourceId, targetId, relationKey })
  }

  // ── Outgoing: read forward AND inverse property values from each selected
  // element. With bidirectional relations, the selected side may carry the
  // inverse key only, so scanning both is required to surface the relation.
  for (const sourceId of selectedIds) {
    const entity = byId.get(sourceId)
    if (!entity) continue
    const props = entity.properties ?? {}

    for (const [relationKey, rel] of relationEntries) {
      for (const key of relationKeysToScan(rel)) {
        const ids = normalizeIds(props[key])
        for (const targetId of ids) {
          tryAdd(sourceId, targetId, relationKey)
        }
      }
    }
  }

  // ── Incoming: scan every element for relations whose forward OR inverse
  // property points into a selected element.
  for (const entity of entities) {
    const otherId = entity.id
    const props = entity.properties ?? {}

    for (const [relationKey, rel] of relationEntries) {
      for (const key of relationKeysToScan(rel)) {
        const ids = normalizeIds(props[key])
        for (const targetId of ids) {
          if (!selectedIds.has(targetId)) continue
          tryAdd(otherId, targetId, relationKey)
        }
      }
    }
  }

  // ── Bidirectional collapse. For each unordered pair {a, b} + relationKey,
  // if we saw both (a → b) and (b → a), emit a single bidirectional line in
  // canonical (lexicographic) source/target order.
  const collapsed = new Map<string, RelationshipLine>()
  for (const r of raw) {
    const lo = r.sourceId < r.targetId ? r.sourceId : r.targetId
    const hi = r.sourceId < r.targetId ? r.targetId : r.sourceId
    const pairKey = `${lo}|${hi}|${r.relationKey}`
    const existing = collapsed.get(pairKey)
    if (existing) {
      // We already have one direction; the other one just arrived → bidirectional.
      existing.bidirectional = true
      continue
    }
    collapsed.set(pairKey, {
      sourceId: r.sourceId,
      targetId: r.targetId,
      relationKey: r.relationKey,
      bidirectional: false,
    })
  }

  return Array.from(collapsed.values())
}

/**
 * Collects relationship lines for the given selection over a dia.Graph.
 *
 * Thin adapter: projects the graph's elements onto {@link RelationshipEntity}
 * and delegates to {@link collectRelationshipLinesFromEntities}. Behavior is
 * unchanged from the original graph-bound implementation.
 */
export function collectRelationshipLines(
  selectedIds: ReadonlySet<string>,
  graph: dia.Graph,
  relationTypes: Record<string, TemplateRelationType> | undefined,
): RelationshipLine[] {
  const entities: RelationshipEntity[] = graph.getElements().map((el) => {
    const data = (el.get('data') ?? {}) as { properties?: Record<string, unknown> }
    return { id: String(el.id), properties: data.properties ?? {} }
  })
  return collectRelationshipLinesFromEntities(selectedIds, entities, relationTypes)
}
